<template>
  <Container indent="wide" class="mb-20 mt-16">
    <!-- FILTER -->
    <div
      class="flex flex-col justify-between gap-4 space-y-6 lg:flex-row lg:items-start lg:gap-16 lg:space-y-0"
    >
      <div>
        <p class="mb-3 inline-block text-sm opacity-40">
          {{ $t('filter.filter_by') }}
        </p>

        <div class="flex flex-wrap gap-3 md:flex-nowrap">
          <!-- LOCATIONS -->
          <AppMultiselect
            v-model="filter.expert_locations"
            :options="locations"
            class="w-full min-w-[13rem] md:w-auto"
            mode="multiple"
            label="label"
            :placeholder="$t('experts_location')"
          >
            <template #icon>
              <MarkerIcon class="w-[0.875rem] text-blue-800" />
            </template>
          </AppMultiselect>

          <!-- EXPERT FIELDS -->
          <AppMultiselect
            v-model="filter.expert_fields"
            :options="expertFields"
            mode="multiple"
            label="label"
            class="w-full min-w-[13rem] md:w-auto md:min-w-[22rem]"
            :placeholder="$t('experts_field')"
          >
            <template #icon>
              <SuitCaseIcon class="w-[1.125rem]" />
            </template>
          </AppMultiselect>
        </div>
      </div>
      <!-- LANGUAGES -->
      <Filter
        :terms="languages"
        :filtered="filter.expert_languages"
        :button-all-text="$t('all')"
        @filter="filter.expert_languages = $event"
      >
        {{ $t('filter.filter_for_languages') }}
      </Filter>
    </div>

    <TransitionGroup
      :key="rerenderKey"
      tag="ul"
      name="list"
      mode="out-in"
      class="relative mt-14 space-y-4"
    >
      <li
        v-for="expert in filteredExperts"
        :key="expert.id"
        class="fade-in w-full"
      >
        <ExpertCard :data="expert" />
      </li>
    </TransitionGroup>

    <!-- SHOW MORE -->
    <!-- <div v-show="hasMorePages" class="mt-14 flex w-full justify-center">
      <AppButton @click="showMore()">
        {{ $t('filter.load_more') }}
      </AppButton>
    </div> -->

    <!-- NO ENTRIES FOUND -->
    <transition name="fade">
      <div
        v-show="filteredExperts.length === 0"
        class="my-10 flex w-full items-center justify-center gap-4 text-blue-dark-800 sm:my-20"
      >
        <MagnifyingLensIcon class="w-[1.125rem] shrink-0" />
        <p class="text-md text-blue-dark-800">
          {{ $t('filter.no_entries_found') }}
        </p>
      </div>
    </transition>
  </Container>
</template>

<script setup>
const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const { locale } = useI18n()
const route = useRoute()
const params = useUrlSearchParams('history')

const experts = ref([])
const locations = ref([])
const expertFields = ref([])
const languages = ref([])

const hasMorePages = ref(null)
const limit = ref(999)

// POPULATE THE FILTER
let routeQueryFilter = route.query[`experts-overview-${props.data.id}`]
if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

const filter = ref(
  routeQueryFilter || {
    expert_locations: [],
    expert_fields: [],
    expert_languages: null,
  }
)

// COMPUTED

// We need to filter the experts in the frontend due to to the AND filter requirement
const filteredExperts = computed(() => {
  let filteredExperts = experts.value

  // LOCATIONS
  if (filter.value.expert_locations.length) {
    const filterSlugs = filter.value.expert_locations.map(
      (location) => location.slug
    )

    filteredExperts = filteredExperts.filter((expert) => {
      return expert.expert_locations.some((location) => {
        return filterSlugs.includes(location.slug)
      })
    })
  }

  // FIELDS
  if (filter.value.expert_fields.length) {
    const filterSlugs = filter.value.expert_fields.map((field) => field.slug)

    filteredExperts = filteredExperts.filter((expert) => {
      return expert.expert_fields.some((field) => {
        return filterSlugs.includes(field.slug)
      })
    })
  }

  return filteredExperts
})

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`experts-overview-${props.data.id}`] = JSON.stringify(newValue)
    fetchExperts()
  },
  { deep: true }
)

// FUNCTIONS
function showMore() {
  limit.value += 8
  fetchExperts()
}

async function fetchExperts() {
  let payload = await useAsyncGql({
    operation: 'Experts',
    variables: {
      limit: limit.value,
      filter: GenerateQueryFilter(),
    },
  })

  payload = payload.data.value?.entries

  hasMorePages.value = payload.has_more_pages

  if (!payload) {
    console.error('Experts could not be fetched')
    return
  }

  experts.value = payload.data
}

function GenerateQueryFilter() {
  const queryFilter = {
    site: locale.value,
  }

  Object.keys(filter.value).forEach((key) => {
    // Do not filter for locations and fields due to we need to filter it in the frontend to have a AND filter
    if (key === 'expert_locations' || key === 'expert_fields') {
      return
    }
    queryFilter[key] = switchContainsOrIn(filter.value[key], true)
  })

  return queryFilter
}

// INITIAL
let payload = await useAsyncGql({
  operation: 'ExpertsAndTerms',
  variables: {
    limit: limit.value,
    filter: GenerateQueryFilter(),
    filterLocale: {
      site: locale.value,
    },
  },
})

payload = payload.data?.value

if (!payload) {
  console.error('Experts could not be fetched')
}

locations.value = payload?.expert_locations?.data

expertFields.value = payload?.expert_fields?.data

languages.value = payload?.expert_languages?.data

experts.value = payload?.entries.data

hasMorePages.value = payload?.entries.has_more_pages

/*
  Rerender the overview if the filter is set in the url.
  his is needed because the ISR cache provides the data without the filter applied -> This results in a wrong data mix (e.g. image and name not corresponding)
  so wie need to rerender the overview with the filter applied.
*/
const rerenderKey = ref(0)
onMounted(() => {
  if (route.query[`experts-overview-${props.data.id}`]) {
    rerenderKey.value++
  }
})
</script>
